import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import ScrollToTop from './ScrollToTop';

const Layout = ({ children }) => {
  const handlePhoneClick = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'appel_telephone', {
        'event_category': 'PAC_Conversion',
        'event_label': 'Contact_Direct'
      });
    }
    
    // Facebook Pixel event
    if (window.fbq) {
      window.fbq('track', 'Contact');
    }
  };
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        {/* Top bar */}
        <div className="bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white py-3">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-between items-center text-sm gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <a href="tel:0188617508" onClick={handlePhoneClick} className="flex items-center gap-2 hover:text-[#E6A835] transition-colors">
                  <Phone size={16} />
                  <span>01 88 61 75 08</span>
                </a>
                <a href="mailto:contact@climatpac81.fr" className="hidden md:flex items-center gap-2 hover:text-[#E6A835] transition-colors">
                  <Mail size={16} />
                  <span>contact@climatpac81.fr</span>
                </a>
              </div>
              
              {/* Certifications logos */}
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-lg shadow-md px-3 py-2">
                  <img 
                    src="/images/qualibat-logo-small.webp" 
                    alt="Qualibat RGE" 
                    className="h-10 md:h-12 w-auto"
                    title="Certifié Qualibat RGE"
                  />
                </div>
                <div className="bg-white rounded-lg shadow-md px-3 py-2">
                  <img 
                    src="/images/qualipac-logo-small.webp" 
                    alt="QualiPAC RGE" 
                    className="h-10 md:h-12 w-auto"
                    title="Certifié QualiPAC RGE"
                  />
                </div>
                <div className="bg-white rounded-lg shadow-md px-3 py-2">
                  <img 
                    src="/images/ventilation-rge-small.webp" 
                    alt="Ventilation+ RGE" 
                    className="h-10 md:h-12 w-auto"
                    title="Certifié Ventilation+ RGE"
                  />
                </div>
                <div className="bg-white rounded-lg shadow-md px-3 py-2">
                  <img 
                    src="/images/qualisol-rge-small.webp" 
                    alt="Qualisol RGE" 
                    className="h-10 md:h-12 w-auto"
                    title="Certifié Qualisol RGE"
                  />
                </div>
              </div>
              
              <div className="hidden lg:flex items-center gap-2">
                <MapPin size={16} />
                <span>Île-de-France + 250km</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <nav className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="/images/logo.webp" alt="Climatpac 81" className="h-16 md:h-20" />
            </Link>

            {/* Desktop menu */}
            <div className="hidden lg:flex items-center gap-8">
              <Link
                to="/"
                className={`font-semibold transition-colors ${
                  isActive('/') ? 'text-[#2D9A68]' : 'text-gray-700 hover:text-[#2D9A68]'
                }`}
              >
                Accueil
              </Link>
              
              {/* Menu Nos Services avec sous-menus */}
              <div className="relative group">
                <span className="font-semibold text-gray-700 hover:text-[#2D9A68] transition-colors cursor-pointer">
                  Nos Services ▾
                </span>
                <div className="absolute top-full left-0 mt-2 w-72 bg-white shadow-2xl rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  <div className="py-2">
                    {/* Pompes à Chaleur */}
                    <div className="px-4 py-2 font-bold text-[#2D9A68] text-sm uppercase border-b">
                      Pompes à Chaleur
                    </div>
                    <Link 
                      to="/services/pompes-a-chaleur#pac-air-eau" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → PAC Air-Eau
                    </Link>
                    <Link 
                      to="/services/pompes-a-chaleur#pac-air-air" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → PAC Air-Air
                    </Link>
                    
                    {/* Eau Chaude Sanitaire */}
                    <div className="px-4 py-2 font-bold text-[#2D9A68] text-sm uppercase border-b border-t mt-1">
                      Eau Chaude Sanitaire
                    </div>
                    <Link 
                      to="/services/eau-chaude-sanitaire#ballon-electrique" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → Ballon Électrique
                    </Link>
                    <Link 
                      to="/services/eau-chaude-sanitaire#ballon-thermodynamique" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → Ballon Thermodynamique
                    </Link>
                    <Link 
                      to="/services/eau-chaude-sanitaire#ballon-solaire" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → Ballon Solaire
                    </Link>
                    
                    {/* Isolation */}
                    <div className="px-4 py-2 font-bold text-[#2D9A68] text-sm uppercase border-b border-t mt-1">
                      Isolation
                    </div>
                    <Link 
                      to="/services/isolation#isolation-combles" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → Combles (Plafond / Rampants)
                    </Link>
                    <Link 
                      to="/services/isolation#isolation-sous-sol" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → Sous-Sol (Planchers Bas)
                    </Link>
                    <Link 
                      to="/services/isolation#isolation-murs" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → Murs Intérieurs
                    </Link>
                    
                    {/* Ventilation */}
                    <div className="px-4 py-2 font-bold text-[#2D9A68] text-sm uppercase border-b border-t mt-1">
                      Ventilation
                    </div>
                    <Link 
                      to="/services/ventilation" 
                      className="block px-6 py-2 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      → VMC Double Flux
                    </Link>
                  </div>
                </div>
              </div>
              
              <Link
                to="/contact"
                className={`font-semibold transition-colors ${
                  isActive('/contact') ? 'text-[#2D9A68]' : 'text-gray-700 hover:text-[#2D9A68]'
                }`}
              >
                Contact
              </Link>
              <Link
                to="/test-eligibilite"
                className="bg-[#E6A835] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#d49520] transition-all hover:shadow-lg"
                data-testid="header-cta-button"
              >
                Testez votre éligibilité
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4" data-testid="mobile-menu">
              <div className="flex flex-col gap-4">
                <Link
                  to="/"
                  className="font-semibold text-gray-700 hover:text-[#2D9A68] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Accueil
                </Link>
                
                {/* Nos Services */}
                <div className="font-semibold text-gray-800 text-sm border-t border-b py-2">NOS SERVICES</div>
                
                {/* Pompes à Chaleur */}
                <div className="font-semibold text-[#2D9A68] text-sm">Pompes à Chaleur</div>
                <Link to="/services/pompes-a-chaleur#pac-air-eau" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → PAC Air-Eau
                </Link>
                <Link to="/services/pompes-a-chaleur#pac-air-air" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → PAC Air-Air
                </Link>
                
                {/* Eau Chaude Sanitaire */}
                <div className="font-semibold text-[#2D9A68] text-sm mt-2">Eau Chaude Sanitaire</div>
                <Link to="/services/eau-chaude-sanitaire#ballon-electrique" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → Ballon Électrique
                </Link>
                <Link to="/services/eau-chaude-sanitaire#ballon-thermodynamique" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → Ballon Thermodynamique
                </Link>
                <Link to="/services/eau-chaude-sanitaire#ballon-solaire" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → Ballon Solaire
                </Link>
                
                {/* Isolation */}
                <div className="font-semibold text-[#2D9A68] text-sm mt-2">Isolation</div>
                <Link to="/services/isolation#isolation-combles" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → Combles
                </Link>
                <Link to="/services/isolation#isolation-sous-sol" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → Sous-Sol
                </Link>
                <Link to="/services/isolation#isolation-murs" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → Murs Intérieurs
                </Link>
                
                {/* Ventilation */}
                <div className="font-semibold text-[#2D9A68] text-sm mt-2">Ventilation</div>
                <Link to="/services/ventilation" className="pl-4 text-gray-600 hover:text-[#2D9A68]" onClick={() => setMobileMenuOpen(false)}>
                  → VMC Double Flux
                </Link>
                
                <Link
                  to="/contact"
                  className="font-semibold text-gray-700 hover:text-[#2D9A68] transition-colors mt-2 border-t pt-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                <Link
                  to="/test-eligibilite"
                  className="bg-[#E6A835] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#d49520] transition-all text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Testez votre éligibilité
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-grow">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* À propos */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#E6A835]">Climatpac 81</h3>
              <p className="text-gray-300 mb-4">
                Votre expert en rénovation énergétique. Certifié Qualibat, QualiPAC, Ventilation+ et Qualisol RGE.
              </p>
              <div className="flex gap-4 flex-wrap">
                <img src="/images/qualibat-logo-small.webp" alt="Qualibat" className="h-12 bg-white rounded p-1" width="132" height="96" />
                <img src="/images/qualipac-logo-small.webp" alt="QualiPAC" className="h-12 bg-white rounded p-1" width="132" height="96" />
                <img src="/images/ventilation-rge-small.webp" alt="Ventilation+" className="h-12 bg-white rounded p-1" width="132" height="96" />
                <img src="/images/qualisol-rge-small.webp" alt="Qualisol" className="h-12 bg-white rounded p-1" width="132" height="96" />
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#E6A835]">Nos Services</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/services/pompes-a-chaleur" className="hover:text-[#2D9A68] transition-colors">Pompes à Chaleur</Link></li>
                <li><Link to="/services/eau-chaude-sanitaire" className="hover:text-[#2D9A68] transition-colors">Eau Chaude Sanitaire</Link></li>
                <li><Link to="/services/isolation" className="hover:text-[#2D9A68] transition-colors">Isolation</Link></li>
                <li><Link to="/services/ventilation" className="hover:text-[#2D9A68] transition-colors">Ventilation VMC</Link></li>
                <li><Link to="/test-eligibilite" className="hover:text-[#E6A835] transition-colors font-semibold">Test d'Éligibilité</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#E6A835]">Contactez-nous</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <Phone size={18} className="mt-1 text-[#2D9A68]" />
                  <a href="tel:0188617508" onClick={handlePhoneClick} className="hover:text-[#2D9A68] transition-colors">01 88 61 75 08</a>
                </li>
                <li className="flex items-start gap-2">
                  <Mail size={18} className="mt-1 text-[#2D9A68]" />
                  <a href="mailto:contact@climatpac81.fr" className="hover:text-[#2D9A68] transition-colors">contact@climatpac81.fr</a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={18} className="mt-1 text-[#2D9A68]" />
                  <span>Île-de-France + 250km</span>
                </li>
              </ul>
            </div>

            {/* Horaires */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#E6A835]">Horaires</h3>
              <ul className="space-y-2 text-gray-300">
                <li><strong>Lundi - Jeudi</strong><br />10h00 - 17h00</li>
                <li><strong>Vendredi</strong><br />10h00 - 15h00</li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p className="mb-3">&copy; 2025 Climatpac 81. Tous droits réservés. | Certifié Qualibat, QualiPAC, Ventilation+ & Qualisol RGE</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/mentions-legales" className="hover:text-[#E6A835] transition-colors">
                Mentions légales
              </Link>
              <span>•</span>
              <Link to="/politique-confidentialite" className="hover:text-[#E6A835] transition-colors">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;